// src/components/roles/RoleWizard/WizardStep3.tsx
import React from 'react';
import { Box, Typography, Paper, Chip, Grid } from '@mui/material';
import { getModuleIcon } from '../../../constants/module-icons';
import { RoleWizardData } from '../../../types/role.types';
import { SYSTEM_STRUCTURE, ModuleConfig, getMenuById } from '../../../config/system-structure';

interface WizardStep3Props {
  formData: RoleWizardData;
}

const WizardStep3: React.FC<WizardStep3Props> = ({ formData }) => {
  const getEnabledModules = (): ModuleConfig[] => {
    return SYSTEM_STRUCTURE.filter((module) => formData.permissions[module.id]?.enabled);
  };

  const getMenuPermissions = (moduleId: string, menuId: string) => {
    const menuPerms = formData.permissions[moduleId]?.menus[menuId];
    if (!menuPerms) {
      return { visible: false, mainButtons: [], rowActions: [], salesFlowActions: [] as string[] };
    }

    const mainButtons = Object.entries(menuPerms.mainButtons)
      .filter(([_, enabled]) => enabled)
      .map(([buttonId]) => buttonId);

    const rowActions = Object.entries(menuPerms.rowActions)
      .filter(([_, enabled]) => enabled)
      .map(([actionId]) => actionId);

    const salesFlowActions = Object.entries(menuPerms.salesFlowActions ?? {})
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    return {
      visible: menuPerms.visible,
      mainButtons,
      rowActions,
      salesFlowActions,
    };
  };

  const getTotalPermissions = (): number => {
    let count = 0;
    Object.values(formData.permissions).forEach((module) => {
      Object.values(module.menus).forEach((menu) => {
        if (menu.visible) count++;
        count += Object.values(menu.mainButtons).filter((val) => val).length;
        count += Object.values(menu.rowActions).filter((val) => val).length;
        if (menu.columnFlags) {
          count += Object.values(menu.columnFlags).filter((val) => val).length;
        }
        if (menu.salesFlowActions) {
          count += Object.values(menu.salesFlowActions).filter((val) => val).length;
        }
      });
    });
    return count;
  };

  const enabledModules = getEnabledModules();
  const totalPermissions = getTotalPermissions();

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Información Básica */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Información Básica
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Nombre del Rol
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {formData.name || '—'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Descripción
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formData.description || '—'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Permisos Asignados */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Permisos Asignados
            </Typography>
            <Chip
              label={`${totalPermissions} permisos`}
              size="small"
              sx={{
                backgroundColor: '#10b981',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>

          {enabledModules.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}
            >
              No se han asignado permisos
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {enabledModules.map((module) => {
                const modulePerms = formData.permissions[module.id];
                const enabledMenus = module.menus.filter(
                  (menu) => modulePerms?.menus[menu.id]?.visible,
                );

                if (enabledMenus.length === 0) return null;

                return (
                  <Box
                    key={module.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: 'rgba(16, 185, 129, 0.05)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: '#10b981',
                          color: 'white',
                        }}
                      >
                        {getModuleIcon(module.id)}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {module.name.toUpperCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ml: 4 }}>
                      {enabledMenus.map((menu) => {
                        const perms = getMenuPermissions(module.id, menu.id);
                        const hasPermissions =
                          perms.mainButtons.length > 0 ||
                          perms.rowActions.length > 0 ||
                          perms.salesFlowActions.length > 0;

                        return (
                          <Box key={menu.id}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                              📄 {menu.name}
                            </Typography>
                            {hasPermissions && (
                              <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {perms.mainButtons.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      Botones:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.mainButtons.map((buttonId) => (
                                        <Chip
                                          key={buttonId}
                                          label={buttonId}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: '#06b6d4',
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {perms.rowActions.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      Acciones:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.rowActions.map((actionId) => (
                                        <Chip
                                          key={actionId}
                                          label={actionId}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {perms.salesFlowActions.length > 0 && (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary', fontWeight: 500 }}
                                    >
                                      {getMenuById(menu.id)?.flowSectionTitle ?? 'Flujos'}:
                                    </Typography>
                                    <Box
                                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
                                    >
                                      {perms.salesFlowActions.map((flowId) => {
                                        const cfg = getMenuById(menu.id)?.salesFlowActions?.find(
                                          (f) => f.id === flowId,
                                        );
                                        return (
                                          <Chip
                                            key={flowId}
                                            label={cfg?.name ?? flowId}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: '0.7rem',
                                              backgroundColor: '#6366f1',
                                              color: 'white',
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default WizardStep3;
